import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GlowCard } from '../components/ui/glow-card';
import { openExternal } from '../utils/telegram';
import { PremiumButton } from '../components/ui/PremiumButton';
import { YCLIENTS_BOOKING_URL } from '../constants';

interface PromotionsPageProps {
  onBack?: () => void;
}

interface Promo {
  id: string;
  title: string;
  description: string;
  price: string;
  conditions?: string;
  imageKey: string;
  bookingUrl?: string;
}

// Auto-load all images from assets/promos/ using Vite's import.meta.glob
// Exclude README.md and other non-image files
const promoImages = import.meta.glob<{ default: string }>('../assets/promos/*.{png,jpg,jpeg,webp,svg,PNG,JPG,JPEG}', { eager: true });

// Create a map of imageKey -> image URL
const imageMap: Record<string, string> = {};
for (const path in promoImages) {
  const fileName = path.split('/').pop()?.split('.')[0];
  if (fileName) {
    imageMap[fileName] = promoImages[path].default;
  }
}

const PROMOTIONS: Promo[] = [
  {
    id: 'first-haircut',
    title: 'Дарим 200 рублей на первое посещение',
    description: 'Скидка 200 ₽ для новых клиентов при первом визите в барбершоп HEADMAN',
    price: '200 ₽',
    conditions: 'Только для новых клиентов',
    imageKey: 'promo_first_haircut',
    bookingUrl: YCLIENTS_BOOKING_URL,
  },
  {
    id: 'father-son',
    title: 'Папа + сын',
    description: 'Семейное предложение для отца и сына\n\nДействует каждое ВОСКРЕСЕНЬЕ\nс 9:00 до 21:00\n\nПри записи к Старшему мастеру\nодновременно на две услуги:\n• мужская стрижка\n• детская стрижка (от 3 до 12 лет)',
    price: 'от 3 200 ₽',
    conditions: 'Совместная стрижка для отца и сына',
    imageKey: 'promo_father_son',
    bookingUrl: YCLIENTS_BOOKING_URL,
  },
  {
    id: 'student',
    title: 'Стрижка студента',
    description: 'Специальное предложение для студентов\n\nДействует только в СРЕДУ\nс 9:00 до 21:00\n\nПри записи к Старшему мастеру\nи предъявлении студенческого билета\nочного отделения',
    price: '1 600 ₽',
    conditions: 'Требуется студенческий билет',
    imageKey: 'promo_student',
    bookingUrl: YCLIENTS_BOOKING_URL,
  },
];

// PromoCard component with loading state
const PromoCard: React.FC<{ promo: Promo; index: number }> = ({ promo, index }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleBooking = () => {
    if (promo.bookingUrl) {
      openExternal(promo.bookingUrl);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 + index * 0.1 }}
    >
      <GlowCard glowColor="gold" customSize>
        <div className="flex flex-col h-full">
          {/* Image container with fixed aspect ratio and contain fit */}
          <div className="relative w-full rounded-xl overflow-hidden bg-gradient-to-br from-sheriff-bg-elevated to-sheriff-bg mb-4 border border-sheriff-border/50" style={{ aspectRatio: '16/10' }}>
            {/* Loading skeleton */}
            {!imageLoaded && !imageError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-sheriff-accent/20 border-t-sheriff-accent rounded-full animate-spin" />
              </div>
            )}
            
            {/* Image */}
            {imageMap[promo.imageKey] && !imageError ? (
              <img
                src={imageMap[promo.imageKey]}
                alt={promo.title}
                loading="lazy"
                decoding="async"
                className={`w-full h-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="text-sm font-medium text-white/40 tracking-wide">HEADMAN</div>
                  <div className="text-xs text-white/25 mt-1">Изображение недоступно</div>
                </div>
              </div>
            )}
          </div>

          {/* Content - compact text below image */}
          <div className="flex-1 flex flex-col px-1">
            <h3 className="text-lg font-medium text-white mb-1.5 leading-tight line-clamp-1 tracking-tight">
              {promo.title}
            </h3>
            <p className="text-sm text-white/60 mb-3 leading-relaxed line-clamp-2">
              {promo.description}
            </p>
            
            {/* Price */}
            <div className="mb-3">
              <span className="text-2xl font-semibold text-white tracking-tight">
                {promo.price}
              </span>
            </div>

            {/* Conditions (small text) */}
            {promo.conditions && (
              <p className="text-xs text-white/45 mb-3 leading-relaxed">
                {promo.conditions}
              </p>
            )}

            {/* CTA Button */}
            <PremiumButton
              fullWidth
              variant="primary"
              onClick={handleBooking}
              className="mt-2"
            >
              Записаться
            </PremiumButton>
          </div>
        </div>
      </GlowCard>
    </motion.div>
  );
};

export const PromotionsPage: React.FC<PromotionsPageProps> = ({ onBack }) => {
  return (
    <div className="space-y-6 pb-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-2"
      >
        <h1 className="text-3xl font-medium text-white tracking-tight">
          Акции
        </h1>
        <p className="text-sm text-sheriff-text-secondary mt-2">
          Выгодные предложения от барбершопа HEADMAN
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-4"
      >
        {PROMOTIONS.map((promo, index) => (
          <PromoCard key={promo.id} promo={promo} index={index} />
        ))}
      </motion.div>

      {onBack && (
        <PremiumButton
          fullWidth
          variant="secondary"
          onClick={onBack}
        >
          ← Назад
        </PremiumButton>
      )}
    </div>
  );
};

